// First-person walker.
//
// Deliberately slow and slightly heavy. The brief asks for footage-someone-shot-on-a-phone, and the
// single biggest thing separating that from "video game" at the locomotion level is speed: game FPS
// characters move at 5-7 m/s, which is a sprint. A person looking at a street walks at ~1.5.

import * as THREE from 'three';
import { WORLD, MODE } from './config.js';
import { TouchControls } from './touch.js';
import { setPlayer } from './telemetry.js';

export class Player {
  constructor(world, camera, dom) {
    this.world = world;
    this.camera = camera;
    this.dom = dom;

    this.pos = new THREE.Vector3(-6.4, WORLD.KERB_H, -26.0);
    this.vel = new THREE.Vector3();
    this.yaw = 0;
    this.pitch = 0;
    this.onGround = true;
    this.bobT = 0;
    this.bobAmt = 0;
    this.moveSpeed = 0;
    this.reachDip = 0;
    this.locked = false;
    this.keys = new Set();
    this.speedScale = 1;
    this.touch = null;

    if (!MODE.shot) this._bindInput();
    // Touch layer replaces pointer lock entirely -- it is not a fallback bolted onto the mouse
    // path, because there is no mouse to fall back from.
    if (!MODE.shot && MODE.mobile) this.touch = new TouchControls(dom);
  }

  _bindInput() {
    const el = this.dom;
    el.addEventListener('click', () => {
      if (!this.locked) el.requestPointerLock();
    });
    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === el;
    });
    document.addEventListener('mousemove', (e) => {
      if (!this.locked) return;
      const s = 0.0021;
      this.yaw -= e.movementX * s;
      this.pitch -= e.movementY * s;
      this.pitch = Math.max(-1.35, Math.min(1.35, this.pitch));
    });
    window.addEventListener('keydown', (e) => {
      this.keys.add(e.code);
      if (e.code === 'ShiftLeft') this.speedScale = WORLD.RUN_SPEED / WORLD.WALK_SPEED;
    });
    window.addEventListener('keyup', (e) => {
      this.keys.delete(e.code);
      if (e.code === 'ShiftLeft') this.speedScale = 1;
    });
    window.addEventListener('blur', () => this.keys.clear());
  }

  /** Park at a fixed capture station. Used by shot mode and the harness. */
  parkAt(station) {
    this.pos.set(station.pos[0], station.pos[1] - WORLD.EYE_H, station.pos[2]);
    const look = new THREE.Vector3(...station.look);
    const eye = new THREE.Vector3(...station.pos);
    const d = look.clone().sub(eye);
    this.yaw = Math.atan2(-d.x, -d.z);
    this.pitch = Math.atan2(d.y, Math.hypot(d.x, d.z));
    this.bobAmt = 0;
    this._applyCamera(station.pos[1]);
    setPlayer(this.pos, station.id);
  }

  /**
    * Third-person chase placement. The look direction is still the player's yaw/pitch; the camera
    * sits TP_BACK behind the focus point along it, raised and shouldered. If the wall behind the
    * player would swallow the lens, the boom pulls in until the world reports clear line of sight
    * -- the same slab test the Sentinel uses to decide whether it can see you.
    */
  _applyChase(focusY, bobX, roll) {
    const cp = Math.cos(this.pitch);
    const dir = [-Math.sin(this.yaw) * cp, Math.sin(this.pitch), -Math.cos(this.yaw) * cp];
    const fx = this.pos.x, fy = this.pos.y + WORLD.TP_FOCUS, fz = this.pos.z;
    const rx = Math.cos(this.yaw), rz = -Math.sin(this.yaw);   // camera right
    // Portrait has far less horizontal room; sitting further back keeps the street visible
    // around the body instead of behind it.
    const portrait = innerWidth / innerHeight < 1.0;
    const BACK = WORLD.TP_BACK * (portrait ? 1.22 : 1);
    let back = BACK;
    for (const d of [BACK, 2.1, 1.5, 1.0, 0.6]) {
      const tx = fx - dir[0] * d + rx * WORLD.TP_SIDE;
      const ty = fy - dir[1] * d + WORLD.TP_UP;
      const tz = fz - dir[2] * d + rz * WORLD.TP_SIDE;
      back = d;
      if (this.world.lineOfSight(fx, fy, fz, tx, ty, tz)) break;
    }
    this.camera.position.set(
      fx - dir[0] * back + rx * WORLD.TP_SIDE + rx * bobX * 0.4,
      fy - dir[1] * back + WORLD.TP_UP,
      fz - dir[2] * back + rz * WORLD.TP_SIDE);
    this.camera.rotation.set(0, 0, 0);
    this.camera.rotateY(this.yaw);
    this.camera.rotateX(this.pitch);
    if (roll) this.camera.rotateZ(roll * 0.3);
  }

  _applyCamera(eyeY) {
    this.camera.position.set(this.pos.x, eyeY, this.pos.z);
    this.camera.rotation.set(0, 0, 0);
    this.camera.rotateY(this.yaw);
    this.camera.rotateX(this.pitch);
  }

  /** Drive the walker directly -- the harness uses this for the mid-motion frame. */
  driveTo(dt, targetX, targetZ) {
    const dx = targetX - this.pos.x, dz = targetZ - this.pos.z;
    const d = Math.hypot(dx, dz);
    if (d < 0.02) return true;
    const sp = WORLD.WALK_SPEED;
    const step = Math.min(d, sp * dt);
    this._move(dt, (dx / d) * step, (dz / d) * step, sp);
    return false;
  }

  update(dt) {
    let fx = 0, fz = 0;
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) fz -= 1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) fz += 1;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) fx -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) fx += 1;

    if (this.touch) {
      // Look first: turning before moving means the step goes where the thumb just aimed.
      const L = this.touch.consumeLook();
      // Per CSS pixel, so the same physical swipe turns the same amount on a phone and a tablet.
      const s = 0.0042;
      this.yaw -= L.x * s;
      this.pitch -= L.y * s;
      this.pitch = Math.max(-1.35, Math.min(1.35, this.pitch));
      if (this.touch.move.x || this.touch.move.z) { fx += this.touch.move.x; fz += this.touch.move.z; }
      this.speedScale = this.touch.run ? WORLD.RUN_SPEED / WORLD.WALK_SPEED : 1;
    }

    const len = Math.hypot(fx, fz);
    let mx = 0, mz = 0, speed = 0;
    if (len > 0) {
      // Normalise DIRECTION, but keep the stick's magnitude as a throttle (clamped to 1) so a
      // touch stick is analogue rather than a four-way pad.
      const throttle = this.touch ? Math.min(1, len) : 1;
      fx /= len; fz /= len;
      speed = WORLD.WALK_SPEED * this.speedScale * throttle;
      const s = Math.sin(this.yaw), c = Math.cos(this.yaw);
      // Forward is -Z rotated by yaw: forward = (-sin, 0, -cos), right = (cos, 0, -sin).
      // move = right*fx + forward*(-fz), which expands to the two lines below.
      //
      // USER-REPORTED DEFECT (amendment to System 1): the previous transform negated the fz terms
      // (`wx = fx*c - fz*s; wz = -fx*s - fz*c`), so W walked backward and S forward. A/D only use
      // the fx terms, which were correct -- which is why only W/S read inverted. No gate ever saw
      // it because every automated capture drives the player via parkAt()/driveTo(), never through
      // the keyboard transform. Verified now by harness/keycheck.mjs, which presses the real keys.
      const wx = c * fx + s * fz;
      const wz = -s * fx + c * fz;
      mx = wx * speed * dt;
      mz = wz * speed * dt;
    }
    this._move(dt, mx, mz, speed);
  }

  _move(dt, mx, mz, speed) {
    const W = this.world;
    const footY = this.pos.y;
    const headY = footY + WORLD.EYE_H + 0.1;

    let nx = this.pos.x + mx;
    let nz = this.pos.z + mz;
    [nx, nz] = W.resolve(nx, nz, WORLD.PLAYER_R, footY, headY);

    // Ground follow. STEP_UP is what lets you walk up a kerb or a stair tread without a jump input,
    // and what stops you stepping straight onto the 6.4 m walkway from the street.
    const g = W.groundAt(nx, nz, footY);
    if (g <= footY + WORLD.STEP_UP) {
      this.pos.x = nx;
      this.pos.z = nz;
    } else {
      // Blocked by a step too tall -- try sliding along each axis separately.
      const gx = W.groundAt(nx, this.pos.z, footY);
      if (gx <= footY + WORLD.STEP_UP) this.pos.x = nx;
      const gz = W.groundAt(this.pos.x, nz, footY);
      if (gz <= footY + WORLD.STEP_UP) this.pos.z = nz;
    }

    const target = W.groundAt(this.pos.x, this.pos.z, this.pos.y);
    if (this.pos.y < target) {
      // Rise smoothly rather than snapping -- a snap on every stair tread reads as a glitch.
      this.pos.y = Math.min(target, this.pos.y + 9.0 * dt);
      this.onGround = true;
    } else if (this.pos.y > target + 0.01) {
      this.vel.y -= WORLD.GRAVITY * dt;
      this.pos.y += this.vel.y * dt;
      if (this.pos.y <= target) { this.pos.y = target; this.vel.y = 0; this.onGround = true; }
      else this.onGround = false;
    } else {
      this.pos.y = target;
      this.vel.y = 0;
      this.onGround = true;
    }

    // Head motion. Two components: a vertical bounce at 2x stride, and a lateral sway at 1x. The
    // lateral one is what actually reads as human -- pure vertical bob reads as a bobbing camera.
    this.moveSpeed = speed;   // published for the avatar's gait clock
    const moving = speed > 0.05;
    this.bobAmt += ((moving ? 1 : 0) - this.bobAmt) * Math.min(1, dt * 6);
    if (moving) this.bobT += dt * (speed / WORLD.WALK_SPEED) * 4.6;

    const bobY = Math.sin(this.bobT * 2) * 0.021 * this.bobAmt;
    const bobX = Math.sin(this.bobT) * 0.026 * this.bobAmt;
    const roll = Math.sin(this.bobT) * 0.0085 * this.bobAmt;

    if (MODE.view === 'tp') {
      this._applyChase(this.pos.y + WORLD.TP_FOCUS, bobX, roll);
    } else {
      const eyeY = this.pos.y + WORLD.EYE_H + bobY - (this.reachDip || 0);
      const s = Math.sin(this.yaw), c = Math.cos(this.yaw);
      this.camera.position.set(this.pos.x + c * bobX, eyeY, this.pos.z - s * bobX);
      this.camera.rotation.set(0, 0, 0);
      this.camera.rotateY(this.yaw);
      this.camera.rotateX(this.pitch);
      this.camera.rotateZ(roll);
    }

    setPlayer(this.pos);
  }
}
